/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { deriveOptions, Directory } from "@cosmos/components";
import { scrollIntoViewEffect } from "@cosmos/components/utils";
import { CosmosEnumDirectoryVariant } from "@cosmos/hygraph-schema";
import { useCallback, useMemo, useRef } from "react";

import { InstructorCard } from "./cards/InstructorCard";
import { ServicePartnerCard } from "./cards/ServicePartnerCard";
import { TrainingPartnerCard } from "./cards/TrainingPartnerCard";

import type { Instructor, ServicePartner, TrainingPartner } from "@cosmos/airtable-data";
import type { FilterDef } from "@cosmos/components";

/**
 * Card grid layout — 3 columns at desktop, 2 at tablet (lgDown), 1 at mobile
 * (smDown). Matches the Figma layout and the canonical Cosmos card-section
 * pattern (BlogCards / FeatureCards / NewsCards).
 */
const CARD_COLUMNS = 3 as const;

const PER_PAGE = 24;

/* -------------------------------------------------------------------------- *
 * CMS chrome — narrowed per variant
 * -------------------------------------------------------------------------- */

/**
 * Grid-heading + filter-chrome fields shared by every directory variant.
 * Maps 1:1 to the Hygraph fragment scalars, sans the `directory*` prefix
 * (the prefix lives on the Hygraph side; the page-data → chrome translation
 * happens in `toCmsBodyData`).
 */
export interface DirectoryBaseChrome {
	eyebrow?: string | null;
	title?: string | null;
	/**
	 * Description rendered as HTML via `CosmosRichText`. The `Html` suffix
	 * mirrors the `DirectoryHeading.descHtml` prop and signals that the
	 * value flows through rich-text rendering, not text escaping.
	 */
	descHtml?: string | null;
	searchPlaceholder?: string | null;
	industryFilterLabel?: string | null;
	noResultsLabel?: string | null;
	/**
	 * Localizable "No results for `{0}`" template — `{0}` is replaced with the
	 * current search query at render time. Defaults to the English template
	 * when null/undefined. Forwarded through `DirectoryConfig.noResultsWithQueryLabel`.
	 */
	noResultsWithQueryTemplate?: string | null;
}

/**
 * ServicePartner-only chrome — adds the combobox label/placeholder for the
 * services filter. `serviceFilterLabel` is required so the rendered combobox
 * always has an accessible name (WCAG 4.1.2). The CMS field is nullable;
 * the page-template mapping supplies an English fallback when null.
 */
export interface DirectoryServiceChrome extends DirectoryBaseChrome {
	serviceFilterLabel: string;
	serviceFilterPlaceholder?: string | null;
}

/* -------------------------------------------------------------------------- *
 * Generic variant body — shared chrome + grid layout for all three variants
 * -------------------------------------------------------------------------- */

interface DirectoryBodyProps<T> {
	cards: T[];
	chrome: DirectoryBaseChrome;
	paramPrefix: string;
	filters: FilterDef<T>[];
	getKey: (item: T) => string;
	renderCard: (item: T) => React.ReactNode;
}

function DirectoryBody<T>({
	cards,
	chrome,
	paramPrefix,
	filters,
	getKey,
	renderCard,
}: DirectoryBodyProps<T>): React.JSX.Element {
	const withQueryTemplate = chrome.noResultsWithQueryTemplate;
	const noResultsWithQueryLabel = withQueryTemplate
		? (query: string) => withQueryTemplate.replace("{0}", query)
		: undefined;
	const sectionRef = useRef<HTMLElement>(null);
	const onPageScroll = useCallback(() => {
		scrollIntoViewEffect(sectionRef.current, 0);
	}, []);
	return (
		<Directory.Section ref={sectionRef}>
			<Directory<T>
				data={cards}
				getKey={getKey}
				paramPrefix={paramPrefix}
				filters={filters}
				pagination={{ perPage: PER_PAGE }}
				noResultsLabel={chrome.noResultsLabel ?? undefined}
				noResultsWithQueryLabel={noResultsWithQueryLabel}
			>
				<Directory.Heading eyebrow={chrome.eyebrow} title={chrome.title} descHtml={chrome.descHtml} />
				<Directory.Filters />
				<Directory.Grid<T> columns={CARD_COLUMNS}>{renderCard}</Directory.Grid>
				<Directory.Pagination onPageScroll={onPageScroll} />
			</Directory>
		</Directory.Section>
	);
}

/* -------------------------------------------------------------------------- *
 * Variant: GoldInstructors
 * -------------------------------------------------------------------------- */

interface InstructorsBodyProps {
	cards: Instructor[];
	chrome: DirectoryBaseChrome;
}

function InstructorsDirectoryBody({ cards, chrome }: InstructorsBodyProps): React.JSX.Element {
	const industryOptions = useMemo(() => deriveOptions(cards, (i) => i.industries), [cards]);

	const filters = useMemo<FilterDef<Instructor>[]>(
		() => [
			{
				kind: "search",
				paramKey: "q",
				placeholder: chrome.searchPlaceholder ?? undefined,
				accessors: [
					{ type: "string", fn: (i) => i.name },
					{ type: "string", fn: (i) => i.country },
					{ type: "string", fn: (i) => i.geo },
					{ type: "array", fn: (i) => i.industries },
				],
			},
			{
				kind: "chips",
				paramKey: "industry",
				label: chrome.industryFilterLabel ?? "Industries",
				matchMode: "any",
				accessor: (i) => i.industries,
				options: industryOptions,
			},
		],
		[chrome.industryFilterLabel, chrome.searchPlaceholder, industryOptions],
	);

	return (
		<DirectoryBody<Instructor>
			cards={cards}
			chrome={chrome}
			paramPrefix="inst"
			filters={filters}
			getKey={(i) => i.id}
			renderCard={(instructor) => <InstructorCard data={instructor} />}
		/>
	);
}

/* -------------------------------------------------------------------------- *
 * Variant: TrainingPartners
 * -------------------------------------------------------------------------- */

interface TrainingPartnersBodyProps {
	cards: TrainingPartner[];
	chrome: DirectoryBaseChrome;
}

function TrainingPartnersDirectoryBody({ cards, chrome }: TrainingPartnersBodyProps): React.JSX.Element {
	const industryOptions = useMemo(() => deriveOptions(cards, (p) => p.industries), [cards]);

	const filters = useMemo<FilterDef<TrainingPartner>[]>(
		() => [
			{
				kind: "search",
				paramKey: "q",
				placeholder: chrome.searchPlaceholder ?? undefined,
				accessors: [
					{ type: "string", fn: (p) => p.name },
					{ type: "string", fn: (p) => p.country },
					{ type: "array", fn: (p) => p.languages },
				],
			},
			{
				kind: "chips",
				paramKey: "industry",
				label: chrome.industryFilterLabel ?? "Industries",
				matchMode: "any",
				accessor: (p) => p.industries,
				options: industryOptions,
			},
		],
		[chrome.industryFilterLabel, chrome.searchPlaceholder, industryOptions],
	);

	return (
		<DirectoryBody<TrainingPartner>
			cards={cards}
			chrome={chrome}
			paramPrefix="tp"
			filters={filters}
			getKey={(p) => p.id}
			renderCard={(partner) => <TrainingPartnerCard data={partner} />}
		/>
	);
}

/* -------------------------------------------------------------------------- *
 * Variant: ServicePartners
 * -------------------------------------------------------------------------- */

interface ServicePartnersBodyProps {
	cards: ServicePartner[];
	chrome: DirectoryServiceChrome;
}

function ServicePartnersDirectoryBody({ cards, chrome }: ServicePartnersBodyProps): React.JSX.Element {
	const industryOptions = useMemo(() => deriveOptions(cards, (p) => p.industries), [cards]);
	const serviceOptions = useMemo(() => deriveOptions(cards, (p) => p.supportType), [cards]);

	const filters = useMemo<FilterDef<ServicePartner>[]>(
		() => [
			{
				kind: "search",
				paramKey: "q",
				placeholder: chrome.searchPlaceholder ?? undefined,
				accessors: [
					{ type: "string", fn: (p) => p.name },
					{ type: "array", fn: (p) => p.countriesSupported },
					{ type: "array", fn: (p) => p.officeLocations },
					{ type: "array", fn: (p) => p.languages },
				],
			},
			{
				kind: "chips",
				paramKey: "industry",
				label: chrome.industryFilterLabel ?? "Industries",
				matchMode: "any",
				accessor: (p) => p.industries,
				options: industryOptions,
			},
			{
				// Support types are a multi-valued field; `combobox` keeps the
				// multi-select UX while showing the placeholder until something's
				// picked. (PRD "Services Offered" → Airtable `Support Type`.)
				kind: "combobox",
				paramKey: "service",
				label: chrome.serviceFilterLabel,
				ariaLabel: (chrome.serviceFilterLabel || chrome.serviceFilterPlaceholder) ?? undefined,
				placeholder: chrome.serviceFilterPlaceholder ?? undefined,
				matchMode: "any",
				accessor: (p) => p.supportType,
				options: serviceOptions,
			},
		],
		[
			chrome.industryFilterLabel,
			chrome.searchPlaceholder,
			chrome.serviceFilterLabel,
			chrome.serviceFilterPlaceholder,
			industryOptions,
			serviceOptions,
		],
	);

	return (
		<DirectoryBody<ServicePartner>
			cards={cards}
			chrome={chrome}
			paramPrefix="sp"
			filters={filters}
			getKey={(p) => p.id}
			renderCard={(partner) => <ServicePartnerCard data={partner} />}
		/>
	);
}

/* -------------------------------------------------------------------------- *
 * Dispatcher
 * -------------------------------------------------------------------------- */

/**
 * Discriminated input for the body — the page template builds this from the
 * Hygraph fragment + Airtable fetch.
 *
 * Service-specific chrome (`serviceFilterLabel`/`serviceFilterPlaceholder`)
 * is narrowed onto the `ServicePartners` branch only — other variants can't
 * be passed unused service filter fields.
 */
export type DirectoryCmsBodyData =
	| { variant: typeof CosmosEnumDirectoryVariant.GoldInstructors; cards: Instructor[]; chrome: DirectoryBaseChrome }
	| {
			variant: typeof CosmosEnumDirectoryVariant.TrainingPartners;
			cards: TrainingPartner[];
			chrome: DirectoryBaseChrome;
	  }
	| {
			variant: typeof CosmosEnumDirectoryVariant.ServicePartners;
			cards: ServicePartner[];
			chrome: DirectoryServiceChrome;
	  };

export interface DirectoryCmsBodyProps {
	data: DirectoryCmsBodyData;
}

/**
 * Dispatches to the variant-specific Directory body. Each variant configures
 * its own filter set and card adapter; chrome is narrowed per variant via
 * the discriminated `data` union.
 *
 * Filter chrome strings come from the CMS via the `chrome` field; English
 * fallbacks live in `@epic-mw/i18n` and are read by the cards themselves.
 */
export function DirectoryCmsBody({ data }: DirectoryCmsBodyProps): React.JSX.Element {
	switch (data.variant) {
		case CosmosEnumDirectoryVariant.GoldInstructors:
			return <InstructorsDirectoryBody cards={data.cards} chrome={data.chrome} />;
		case CosmosEnumDirectoryVariant.TrainingPartners:
			return <TrainingPartnersDirectoryBody cards={data.cards} chrome={data.chrome} />;
		case CosmosEnumDirectoryVariant.ServicePartners:
			return <ServicePartnersDirectoryBody cards={data.cards} chrome={data.chrome} />;
		default: {
			// Exhaustive check — TS catches new variants at compile time;
			// runtime throws (matches every other variant dispatcher in
			// cosmos-page-directory) so a typed bypass fails consistently.
			const exhaustiveCheck: never = data;
			throw new Error(
				`[DirectoryCmsBody] Unhandled variant: ${String((exhaustiveCheck as { variant?: string }).variant)}`,
			);
		}
	}
}
